import { useEffect, useMemo, useRef, useState } from "react";
import type { Confession, Lang, MoonResponse } from "./lib/types";
import { App as CapApp } from "@capacitor/app";
import { runMoon } from "./lib/respond";
import { initAds, requestATT } from "./lib/ads";
import { isNative } from "./lib/native";
import { initAnalytics, track, lengthBucket } from "./lib/analytics";
import { STRINGS } from "./i18n/strings";
import type { LegalKind } from "./content/legal";
import Splash from "./screens/Splash";
import Home from "./screens/Home";
import Weighing from "./screens/Weighing";
import Ad from "./screens/Ad";
import Letter from "./screens/Letter";
import Card from "./screens/Card";
import Crisis from "./screens/Crisis";
import ErrorCard from "./screens/ErrorCard";
import ReportModal from "./screens/ReportModal";
import type { ReportBranch } from "./lib/report";
import Settings from "./screens/Settings";
import Legal from "./screens/Legal";
import StoreFab from "./screens/StoreFab";

export type Phase =
  | "splash"
  | "home"
  | "settings"
  | "legal"
  | "weighing"
  | "ad"
  | "letter"
  | "card"
  | "crisis"
  | "error";

const LANGS: Lang[] = ["ko", "en", "ja"];

function detectLang(): Lang {
  const saved = localStorage.getItem("hyuh_lang") as Lang | null;
  if (saved && LANGS.includes(saved)) return saved;
  const l = navigator.language?.toLowerCase() ?? "en";
  if (l.startsWith("ko")) return "ko";
  if (l.startsWith("ja")) return "ja";
  return "en";
}

export default function App() {
  const [phase, setPhase] = useState<Phase>("splash");
  const [lang, setLangState] = useState<Lang>(detectLang());
  const [response, setResponse] = useState<MoonResponse | null>(null);
  const [legalKind, setLegalKind] = useState<LegalKind>("privacy");
  const lastConfessionRef = useRef<Confession | null>(null); // 에러면 재시도용
  const [reportBranch, setReportBranch] = useState<ReportBranch | null>(null); // 응답 신고 모달
  const t = useMemo(() => STRINGS[lang], [lang]);

  useEffect(() => {
    void initAds();
    void initAnalytics().then(() => track("app_open"));

    // iOS ATT — requestATT가 내부에서 "active 될 때까지 대기 + 프롬프트 응답까지 재시도"를 담당한다.
    // 여기선 두 시점에서 발동만 하면 됨(멱등): ① 마운트 즉시(콜드 런치 대비) ② 포그라운드 복귀 시.
    // ①이 이미 진행 중이면 attInFlight 가드로 중복 안 됨. 스플래시(1.4초) 동안 자연히 active 확인됨.
    void requestATT();
    let remove: (() => void) | undefined;
    void CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void requestATT();
    }).then((h) => {
      remove = () => h.remove();
    });
    return () => remove?.();
  }, []);

  function setLang(l: Lang) {
    setLangState(l);
    localStorage.setItem("hyuh_lang", l);
  }

  // 제출 → 헤아림으로 가며 곧장 응답 요청. 원문은 runMoon으로만 흘러가고 어디에도 저장하지 않는다.
  function handleSubmit(c: Confession) {
    lastConfessionRef.current = c; // 재시도용
    setResponse(null);
    localStorage.removeItem("hyuh_draft"); // 제출 즉시 초안 삭제
    track("confession_submitted", { lang, length: lengthBucket(c.text.length), mode: c.mode });
    setPhase("weighing");
    void runMoon(c, lang).then((r) => setResponse(r));
  }

  // 에러면에서 "다시 시도" — 같은 고백으로 재요청.
  function retry() {
    const c = lastConfessionRef.current;
    if (c) handleSubmit(c);
  }

  // 헤아림 최소 노출 후 분기. 위기면 안전면, AI 실패면 에러면, 아니면 게이트1(광고) → 편지.
  function handleWeighingDone() {
    if (!response) return;
    if (response.error) {
      track("ai_error", { lang, kind: response.error });
      setPhase("error");
      return;
    }
    if (response.crisis) {
      track("crisis_shown", { lang, gate: response.gate ?? "self_crisis" });
      setPhase("crisis");
      return;
    }
    track("letter_ready", { lang, register: response.register, fallback: response.fallback ? 1 : 0 });
    // 네이티브만 AdMob 전면 게이트. 웹은 Auto ads(Vignette)가 전환 시 알아서 띄우므로
    // 빈 게이트 화면을 건너뛰고 곧장 편지로(빈 AdSense 플레이스홀더 제거 — §9).
    setPhase(isNative() ? "ad" : "letter");
  }

  function reset() {
    setResponse(null);
    setPhase("home");
  }

  return (
    <div className="app">
      {phase === "splash" && <Splash lang={lang} onDone={() => setPhase("home")} />}

      {phase === "home" && (
        <Home t={t} lang={lang} setLang={setLang} onSettings={() => setPhase("settings")} onSubmit={handleSubmit} />
      )}

      {phase === "settings" && (
        <Settings
          t={t}
          lang={lang}
          setLang={setLang}
          onBack={() => setPhase("home")}
          onOpenLegal={(k) => {
            setLegalKind(k);
            setPhase("legal");
          }}
        />
      )}

      {phase === "legal" && <Legal t={t} lang={lang} kind={legalKind} onBack={() => setPhase("settings")} />}

      {phase === "weighing" && <Weighing t={t} ready={response !== null} onDone={handleWeighingDone} />}

      {phase === "ad" && <Ad onDone={() => setPhase("letter")} />}

      {phase === "letter" && response && (
        <Letter t={t} oneLine={response.oneLine} letter={response.letter} prophecy={response.prophecy} onBack={reset} onNext={() => setPhase("card")} />
      )}

      {phase === "card" && response && (
        <Card t={t} lang={lang} response={response} onBack={() => setPhase("letter")} onReplay={reset} />
      )}

      {phase === "crisis" && <Crisis t={t} lang={lang} gate={response?.gate} onClose={reset} />}

      {phase === "error" && response?.error && (
        <ErrorCard t={t} kind={response.error} onRetry={retry} onBack={reset} />
      )}

      {(phase === "letter" || phase === "crisis") && response && (
        <button className="report-trigger" onClick={() => setReportBranch(phase === "crisis" ? "crisis_safe" : "letter")}>
          {t.reportTrigger}
        </button>
      )}
      {reportBranch && (
        <ReportModal
          t={t}
          lang={lang}
          branch={reportBranch}
          inputText={lastConfessionRef.current?.text ?? ""}
          responseText={reportBranch === "letter" ? (response?.letter ?? "") : ""}
          onClose={() => setReportBranch(null)}
        />
      )}

      {/* 웹 전용 — 모든 페이지 고정 앱 다운로드 플로팅 버튼(우측). 네이티브/앱인토스엔 안 보임. */}
      <StoreFab />
    </div>
  );
}
